import { createContext, useContext, useEffect, useRef, useState } from "react";

/**
 * PersonaContext v2 · adds three identity behaviors:
 *
 *   · Signing session · every Confirm identity refreshes an in-memory
 *     signature good for 5 minutes of activity. Idle beyond that drops
 *     the signature and logs a system event; the next approval forces
 *     a fresh Confirm identity click.
 *
 *   · Delegation · a holder of production-commitment authority can
 *     temporarily grant it to another persona for a bounded window.
 *     Grant and revoke (manual or automatic on expiration) both write
 *     to a dynamic audit stream that the audit log renders alongside
 *     the fixture entries.
 *
 *   · Fictional wall clock · runtime-generated audit timestamps are
 *     stamped "May 23 HH:MM" tied to the demo continuum, starting at
 *     09:14 and ticking forward with real elapsed time from boot.
 */

// ── Personas ────────────────────────────────────────────────────────────────

// Meridian's IT deployment federates through a single provider.
// Surfaced in the sign-in redirect and on the avatar tooltip so IT-flavored
// buyers can visually confirm the federation edge without a slide.
export const identityProvider = "Meridian Azure AD";

export const personas = {
  jokafor: {
    id: "jokafor",
    name: "J. Okafor",
    initials: "JO",
    role: "Executive · COO",
    email: "j.okafor@meridiansurgical.com",
    authorities: ["production-commitment", "genealogy-read"],
    home: "/",
    canDelegate: ["production-commitment"],
  },
  rdiaz: {
    id: "rdiaz",
    name: "R. Diaz",
    initials: "RD",
    role: "Quality Engineer",
    email: "r.diaz@meridiansurgical.com",
    authorities: ["quality-disposition"],
    home: "/quality",
    canDelegate: [],
  },
};

export const authorityLabels = {
  "production-commitment":
    "Requires production commitment authority · J. Okafor",
  "quality-disposition":
    "Requires quality disposition authority · R. Diaz",
};

export const authorityShort = {
  "production-commitment": "production-commitment",
  "quality-disposition": "quality-disposition",
};

// ── Fictional clock ─────────────────────────────────────────────────────────

const BASE_DAY_MS = 9 * 3600 * 1000 + 14 * 60 * 1000; // 09:14

function formatDemoTime(realNowMs, bootMs) {
  const elapsed = realNowMs - bootMs;
  const fictional = BASE_DAY_MS + elapsed;
  const h = Math.floor(fictional / 3600000) % 24;
  const m = Math.floor(fictional / 60000) % 60;
  return `May 23 ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatHM(realNowMs, bootMs) {
  const elapsed = realNowMs - bootMs;
  const fictional = BASE_DAY_MS + elapsed;
  const h = Math.floor(fictional / 3600000) % 24;
  const m = Math.floor(fictional / 60000) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatMMSS(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(s / 60)).padStart(1, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

// ── Session timing ──────────────────────────────────────────────────────────

const SESSION_MS = 5 * 60 * 1000; // 5 minutes idle → drop

// ── Context ─────────────────────────────────────────────────────────────────

const PersonaContext = createContext(null);

export function PersonaProvider({ children }) {
  const [activeId, setActiveId] = useState(null);
  const [lastAuthAt, setLastAuthAt] = useState(null);
  const [lastActivityAt, setLastActivityAt] = useState(Date.now());
  const [delegation, setDelegationState] = useState(null);
  const [dynamicAudit, setDynamicAudit] = useState([]);
  const [now, setNow] = useState(Date.now());
  const boot = useRef(Date.now());

  const active = activeId ? personas[activeId] : null;

  // 1-Hz tick drives every countdown and the two idle checks below.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Real user activity bumps the freshness window (rolling).
  useEffect(() => {
    const bump = () => setLastActivityAt(Date.now());
    window.addEventListener("mousedown", bump);
    window.addEventListener("keydown", bump);
    return () => {
      window.removeEventListener("mousedown", bump);
      window.removeEventListener("keydown", bump);
    };
  }, []);

  // Signing session drop · fires exactly once because setLastAuthAt(null)
  // makes the guard false on the next tick.
  useEffect(() => {
    if (lastAuthAt !== null && Date.now() - lastActivityAt > SESSION_MS) {
      const ts = formatDemoTime(Date.now(), boot.current);
      const who = active?.name || "unknown";
      setLastAuthAt(null);
      setDynamicAudit((prev) => [
        {
          id: `sess-drop-${Date.now()}`,
          timestamp: ts,
          actor: "SYSTEM",
          isSystem: true,
          kind: "session",
          action: "Signing session dropped · idle timeout",
          detail: `signature no longer current · next approval requires re-authentication · previous auth by ${who}`,
        },
        ...prev,
      ]);
    }
  }, [now, lastAuthAt, lastActivityAt, active]);

  // Delegation auto-expiration.
  useEffect(() => {
    if (delegation && Date.now() >= delegation.expiresAtReal) {
      const ts = formatDemoTime(Date.now(), boot.current);
      const d = delegation;
      setDelegationState(null);
      setDynamicAudit((prev) => [
        {
          id: `deleg-exp-${Date.now()}`,
          timestamp: ts,
          actor: "SYSTEM",
          isSystem: true,
          kind: "revoke",
          action: `Authority delegated · window expired: ${authorityShort[d.authority]} ← ${personas[d.to].name}`,
          detail: `basis: window expired · original holder ${personas[d.from].name}`,
        },
        ...prev,
      ]);
    }
  }, [now, delegation]);

  // Compute derived session state each render.
  const idleMs = Math.max(0, now - lastActivityAt);
  const signatureFresh =
    lastAuthAt !== null && idleMs < SESSION_MS;
  const sessionSecondsRemaining = signatureFresh
    ? Math.max(0, Math.floor((SESSION_MS - idleMs) / 1000))
    : 0;
  const lastAuthAtLabel = lastAuthAt
    ? formatHM(lastAuthAt, boot.current)
    : null;

  // hasAuthority · considers active persona's own authorities plus any
  // active delegation targeting the active persona.
  const hasAuthority = (a) => {
    if (!active) return false;
    if (active.authorities.includes(a)) return true;
    if (
      delegation &&
      delegation.to === active.id &&
      delegation.authority === a &&
      Date.now() < delegation.expiresAtReal
    ) {
      return true;
    }
    return false;
  };

  // Only the persona holding the base authority can delegate it.
  const canDelegate = (a) => !!active?.canDelegate?.includes(a) && !delegation;

  const noteAuthentication = () => {
    const t = Date.now();
    setLastAuthAt(t);
    setLastActivityAt(t);
  };

  const setDelegation = ({ to, authority, durationMinutes, reason }) => {
    if (!active) return;
    const nowReal = Date.now();
    const expiresAtReal = nowReal + durationMinutes * 60 * 1000;
    const record = {
      from: active.id,
      to,
      authority,
      durationMinutes,
      reason,
      grantedAtReal: nowReal,
      expiresAtReal,
    };
    setDelegationState(record);
    const ts = formatDemoTime(nowReal, boot.current);
    const expiresLabel = formatHM(expiresAtReal, boot.current);
    setDynamicAudit((prev) => [
      {
        id: `deleg-grant-${nowReal}`,
        timestamp: ts,
        actor: `${active.name} (${active.role})`,
        isSystem: false,
        kind: "grant",
        action: `Authority delegated: ${authorityShort[authority]} → ${personas[to].name} (${personas[to].role})`,
        detail: `expires ${expiresLabel} · window ${durationMinutes}m${reason ? ` · basis: "${reason}"` : ""}`,
      },
      ...prev,
    ]);
  };

  const revokeDelegation = () => {
    if (!delegation || !active) return;
    const d = delegation;
    const nowReal = Date.now();
    setDelegationState(null);
    setDynamicAudit((prev) => [
      {
        id: `deleg-revoke-${nowReal}`,
        timestamp: formatDemoTime(nowReal, boot.current),
        actor: `${active.name} (${active.role})`,
        isSystem: false,
        kind: "revoke",
        action: `Authority delegated · revoked: ${authorityShort[d.authority]} ← ${personas[d.to].name}`,
        detail: `basis: manual revoke by ${active.name}`,
      },
      ...prev,
    ]);
  };

  const delegationExpiresLabel = delegation
    ? formatHM(delegation.expiresAtReal, boot.current)
    : null;
  const delegationSecondsRemaining = delegation
    ? Math.max(0, Math.floor((delegation.expiresAtReal - now) / 1000))
    : 0;

  const value = {
    active,
    personas,
    setActiveId,
    signOut: () => {
      setActiveId(null);
      setLastAuthAt(null);
    },
    hasAuthority,
    canDelegate,
    // Signing session
    signatureFresh,
    sessionSecondsRemaining,
    lastAuthAtLabel,
    noteAuthentication,
    formatSessionCountdown: () => formatMMSS(sessionSecondsRemaining),
    // Delegation
    delegation,
    setDelegation,
    revokeDelegation,
    delegationExpiresLabel,
    delegationSecondsRemaining,
    formatDelegationCountdown: () => formatMMSS(delegationSecondsRemaining),
    // Audit
    dynamicAudit,
    formatDemoNow: () => formatDemoTime(Date.now(), boot.current),
  };

  return (
    <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>
  );
}

export function usePersona() {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error("usePersona must be used inside PersonaProvider");
  return ctx;
}
